import { useRouter } from 'next/router';
import { useAuthenticated } from '@lib/user-context';
import Button from '@components/button';
import Icon from '@components/icon';
import Link from '@components/link';
import EnhancedContactForm from '@components/enhanced-form';
import FormRealtorCards from '@components/enhanced-form/realtor-cards';
import BrokerReciprocityCard from '@components/broker-reciprocity-card';
import { pushDataLayerEvent } from '@lib/gtm';
import { useListingContext } from '../../context';

import {
  overview_heading,
  summary,
  concession_note_container,
  seller_concession_note,
  directions_container,
  directions_link,
  refresh_date,
  buttons,
  mobile_button,
  center
} from './styles.module.scss';

const PropertySummary = ({
  setNotificationModalIsOpen,
  setNotesModalIsOpen,
  setShareModalIsOpen,
  setPreApprovalModalIsOpen,
  setRequestATourModalIsOpen
}) => {
  const router = useRouter();
  const { isAuthenticated: isLoggedIn } = useAuthenticated();

  const {
    address,
    marketingRemarks,
    statusId,
    directions,
    hasMapLocationExternalLink,
    mapLocationExternalLink,
    dataRefreshed,
    hasTour,
    hasPrimaryAndSecondaryAgent,
    informationRequestDefaultComments,
    listingId: id,
    displayAgents,
    userInfo
  } = useListingContext();

  const handleOpenNotificationModal = () => {
    pushDataLayerEvent('listing_notification_modal_click', router.asPath);
    if (isLoggedIn) {
      setNotificationModalIsOpen(true);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login';
      } else {
        router.push('/auth/login');
      }
    }
  };

  const handleOpenNotesModal = () => {
    pushDataLayerEvent('listing_notes_modal_click', router.asPath);
    if (isLoggedIn) {
      setNotesModalIsOpen(true);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login';
      } else {
        router.push('/auth/login');
      }
    }
  };

  const handleOpenShareModal = () => {
    pushDataLayerEvent('listing_share_modal_click', router.asPath);
    setShareModalIsOpen(true);
  };

  const shareButtons = (
    <div className={buttons}>
      <span
        className={mobile_button}
        onClick={handleOpenNotificationModal}
        onKeyDown={handleOpenNotificationModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="notification"
          iconType="actions"
          label="Notify Me"
          theme="text-only"
          onClick={handleOpenNotificationModal}
        />
      </span>
      <span
        className={mobile_button}
        onClick={handleOpenNotesModal}
        onKeyDown={handleOpenNotesModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="notes"
          iconType="actions"
          label="Add Note"
          theme="text-only"
          onClick={handleOpenNotesModal}
        />
      </span>
      <span
        className={mobile_button}
        onClick={handleOpenShareModal}
        onKeyDown={handleOpenShareModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="share"
          iconType="actions"
          label="Share"
          theme="text-only"
          onClick={handleOpenShareModal}
        />
      </span>
    </div>
  );

  return (
    <>
      <h2 className={overview_heading}>More About {address}</h2>

      <div className={summary}>
        {marketingRemarks}

        <div className={concession_note_container}>
          {statusId === 6 ? (
            <>
              <Button
                label="Get Property Estimate"
                url={`https://homevalue.sibcycline.com/${
                  displayAgents[0]?.agentId || router?.query?.agentid || ''
                }`}
                dataLayerEvent="listing_get_property_estimate_click"
              />
              <div className={seller_concession_note}>
                <Icon type="general" name="flame"></Icon>
                <p>How does your home compare?</p>
              </div>
            </>
          ) : (
            <>
              <Button
                label="Get Pre-Approved"
                onClick={() => {
                  pushDataLayerEvent('listing_pre_approval_modal_click', router.asPath);
                  setPreApprovalModalIsOpen(true);
                }}
              />
              <div className={seller_concession_note}>
                <Icon type="general" name="flame"></Icon>
                <p>Connect with a loan officer to get started!</p>
              </div>
            </>
          )}
        </div>

        {(directions || hasMapLocationExternalLink) && statusId != 6 && (
          <div className={directions_container}>
            {hasMapLocationExternalLink ? (
              <Link
                url={mapLocationExternalLink}
                target="_blank"
                className={directions_link}
                dataLayerEvent="listing_directions_click"
              >
                <Icon type="filters" name="location"></Icon>
                <h3>Directions to this Listing</h3>
              </Link>
            ) : (
              <h3>Directions to this Listing</h3>
            )}
            <p>{directions && `: ${directions}`}</p>
          </div>
        )}
        {dataRefreshed && (
          <div className={refresh_date}>
            <p>Information Refreshed: {dataRefreshed}</p>
          </div>
        )}
        {shareButtons}
      </div>

      <div className={center}>
        {displayAgents[0] || !!userInfo.agent ? (
          <EnhancedContactForm
            realtors={displayAgents}
            defaultCommentValue={informationRequestDefaultComments}
            hasTour={hasTour}
            hasPrimaryAndSecondaryAgent={hasPrimaryAndSecondaryAgent}
            listingId={id}
            agentIds={displayAgents?.map((agent) => agent.agentId) || []}
            sticky={true}
            setModalIsOpen={setRequestATourModalIsOpen}
            requestType="Listing"
            shouldAllowLeadsPlusAgent
          >
            <FormRealtorCards
              hasTour={hasTour}
              hasPrimaryAndSecondaryAgent={hasPrimaryAndSecondaryAgent}
              setModalIsOpen={setRequestATourModalIsOpen}
              realtors={displayAgents}
              onListingPage={true}
            />
          </EnhancedContactForm>
        ) : (
          <BrokerReciprocityCard
            hasTour={hasTour}
            listingId={id}
            defaultCommentValue={informationRequestDefaultComments}
            sticky={true}
            setModalIsOpen={setRequestATourModalIsOpen}
          />
        )}
      </div>
    </>
  );
};

export default PropertySummary;
